import React, { useState } from "react";
import hiddenInVatar from "../vatar/hiddenInVatar";
import useCoinStore from "../stores/coinStore";
import {
  POWER_UP_ITEMS,
  purchasePowerUp,
  hasUsedOffense,
  hasUsedDefense,
  hasShield,
  isConsecutiveTargetBlocked,
} from "../Services/powerUpService";
import CoinStore from "./CoinStore";
import {
  Swords,
  Shield,
  Hand,
  Banknote,
  Skull,
  Ban,
  Snowflake,
  Crosshair,
  Scale,
  ClipboardCopy,
} from "lucide-react";

const ICON_MAP = {
  hand: Hand,
  banknote: Banknote,
  skull: Skull,
  ban: Ban,
  snowflake: Snowflake,
  crosshair: Crosshair,
  scale: Scale,
  "clipboard-copy": ClipboardCopy,
  shield: Shield,
};

const OFFENSE_ITEMS = Object.values(POWER_UP_ITEMS).filter(
  (i) => i.category === "offense"
);
const DEFENSE_ITEMS = Object.values(POWER_UP_ITEMS).filter(
  (i) => i.category === "defense"
);

function PowerUpShop({ roomData, roomDocId, currentRound, onClose }) {
  const coinStore = useCoinStore();
  const currentUserId = localStorage.getItem("currentUserId");

  const [step, setStep] = useState("browse"); // browse | select_target | select_category | confirm
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null); // { userId, name }
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [showCoinStore, setShowCoinStore] = useState(false);
  const [activeTab, setActiveTab] = useState("offense");

  const otherPlayers = (roomData?.players || []).filter(
    (p) => p.id !== currentUserId
  );
  const categories = roomData?.categories || ["name", "animal", "place", "thing"];

  // Check whether each item is usable right now
  const offenseUsed = hasUsedOffense(roomData, currentUserId);
  const defenseUsed = hasUsedDefense(roomData, currentUserId);
  const playerHasShield = hasShield(roomData, currentUserId);

  function getItemBlockReason(item) {
    if (!coinStore.canAfford(item.cost)) return "Not enough coins";
    if (item.category === "offense" && offenseUsed)
      return "Already used offense this round";
    if (item.category === "defense" && defenseUsed)
      return "Already used defense this round";
    if (item.type === "shield" && playerHasShield) return "You already have a shield";
    return null;
  }

  function handleSelectItem(item) {
    const reason = getItemBlockReason(item);
    if (reason) {
      setError(reason);
      return;
    }
    setError("");
    setSelectedItem(item);

    if (!item.requiresTarget) {
      // Shield — go straight to confirm
      setStep("confirm");
    } else if (otherPlayers.length === 1) {
      // Only one target — auto-select them
      setSelectedTarget({ userId: otherPlayers[0].id, name: otherPlayers[0].name });
      setStep(item.requiresCategory ? "select_category" : "confirm");
    } else {
      setStep("select_target");
    }
  }

  function handleSelectTarget(player) {
    const blocked = isConsecutiveTargetBlocked(roomData, currentUserId, player.id);
    if (blocked) {
      setError(`Can't target ${player.name} again — 3 rounds in a row limit.`);
      return;
    }
    setError("");
    setSelectedTarget({ userId: player.id, name: player.name });
    setStep(selectedItem?.requiresCategory ? "select_category" : "confirm");
  }

  function handleSelectCategory(cat) {
    setSelectedCategory(cat);
    setStep("confirm");
  }

  async function handleConfirm() {
    setLoading(true);
    setError("");
    const result = await purchasePowerUp({
      itemId: selectedItem.id,
      targetUserId: selectedTarget?.userId || null,
      targetName: selectedTarget?.name || null,
      category: selectedCategory || null,
      roomDocId,
      roomData,
      coinStore,
    });
    setLoading(false);

    if (!result.success) {
      setError(result.message || "Something went wrong.");
      return;
    }

    const isShareFate = selectedItem.type === "share_fate";
    setSuccessMsg(
      isShareFate
        ? `Dead Weight activated on ${selectedTarget.name}!`
        : `${selectedItem.name} queued for round ${currentRound + 1}!`
    );
    // Auto-close after a beat
    setTimeout(onClose, 1800);
  }

  function reset() {
    setStep("browse");
    setSelectedItem(null);
    setSelectedTarget(null);
    setSelectedCategory(null);
    setError("");
  }

  // ── Render helpers ──────────────────────────────────────────────────────────

  function renderItemCard(item) {
    const blockReason = getItemBlockReason(item);
    const isBlocked = !!blockReason;
    const nextRound = item.type === "share_fate" ? currentRound : currentRound + 1;

    return (
      <div
        key={item.id}
        className={`rounded-xl border-2 p-3 transition-all ${isBlocked
          ? "border-gray-200 bg-gray-50 opacity-60"
          : "border-gray-200 bg-white hover:border-purple-300 hover:shadow-sm cursor-pointer active:scale-95"
          }`}
        onClick={() => !isBlocked && handleSelectItem(item)}
      >
        <div className="flex items-start gap-3">
          {(() => {
            const IconComp = ICON_MAP[item.iconName];
            return IconComp ? (
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${item.category === "offense" ? "bg-red-100 text-red-600" : "bg-blue-100 text-blue-600"}`}>
                <IconComp className="w-5 h-5" />
              </div>
            ) : (
              <span className="text-2xl flex-shrink-0">{item.emoji}</span>
            );
          })()}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="font-bold text-gray-900 text-sm">{item.name}</span>
              <span className="text-xs font-bold text-red-700 bg-red-50 px-2 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1">
                <span className="w-4 h-4 bg-red-600 rounded-full flex items-center justify-center text-white text-[8px] font-bold">₦</span>
                {item.cost}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5 leading-snug">{item.desc}</p>
            <p className="text-xs text-purple-500 mt-1">
              {item.type === "share_fate"
                ? "Applies immediately"
                : `Applies round ${nextRound}`}
            </p>
            {isBlocked && (
              <p className="text-xs text-red-500 font-semibold mt-1">{blockReason}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Steps ───────────────────────────────────────────────────────────────────

  if (successMsg) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl">
          <div className="text-5xl mb-3">✅</div>
          <p className="text-lg font-bold text-gray-800">{successMsg}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
        <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[90vh] flex flex-col shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              {step !== "browse" && (
                <button
                  onClick={reset}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded-lg"
                >
                  ←
                </button>
              )}
              <h2 className="font-bold text-gray-900 text-lg">
                {step === "browse" && "Power-Up Shop"}
                {step === "select_target" && "Choose Target"}
                {step === "select_category" && "Choose Category"}
                {step === "confirm" && "Confirm Purchase"}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 bg-red-50 px-2.5 py-1 rounded-full">
                <span className="w-4 h-4 bg-red-600 rounded-full flex items-center justify-center text-white text-[8px] font-bold">₦</span>
                <span className="text-sm font-bold text-red-700">{coinStore.coinBalance}</span>
              </div>
              <button
                onClick={() => setShowCoinStore(true)}
                className="w-7 h-7 flex items-center justify-center bg-red-600 hover:bg-red-700 rounded-lg transition-colors shadow-sm"
                title="Buy Coins"
              >
                <span className="text-white text-lg font-bold leading-none">+</span>
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-xl w-8 h-8 flex items-center justify-center rounded-lg"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Error banner */}
          {error && (
            <div className="mx-4 mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-semibold">
              {error}
            </div>
          )}

          {/* Body */}
          <div className="overflow-y-auto flex-1">

            {/* ── BROWSE ── */}
            {step === "browse" && (
              <div className="p-4">

                {/* Status indicators */}
                {(offenseUsed || defenseUsed || playerHasShield) && (
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {offenseUsed && (
                      <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full font-medium">
                        Offense used
                      </span>
                    )}
                    {defenseUsed && (
                      <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-medium">
                        Defense used
                      </span>
                    )}
                    {playerHasShield && (
                      <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full font-medium">
                        Shield active
                      </span>
                    )}
                  </div>
                )}

                {/* Tabs */}
                <div className="flex rounded-xl overflow-hidden border border-gray-200 mb-4">
                  <button
                    onClick={() => setActiveTab("offense")}
                    className={`flex-1 py-2 text-sm font-semibold transition-colors ${activeTab === "offense"
                      ? "bg-red-600 text-white"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                      }`}
                  >
                    <Swords className="w-4 h-4 inline-block mr-1 -mt-0.5" /> Offense
                  </button>
                  <button
                    onClick={() => setActiveTab("defense")}
                    className={`flex-1 py-2 text-sm font-semibold transition-colors ${activeTab === "defense"
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                      }`}
                  >
                    <Shield className="w-4 h-4 inline-block mr-1 -mt-0.5" /> Defense
                  </button>
                </div>

                <div className="space-y-2">
                  {activeTab === "offense"
                    ? OFFENSE_ITEMS.map(renderItemCard)
                    : DEFENSE_ITEMS.map(renderItemCard)}
                </div>

                <p className="text-center text-xs text-gray-400 mt-4">
                  1 offense + 1 defense per round • effects apply next round
                </p>
              </div>
            )}

            {/* ── SELECT TARGET ── */}
            {step === "select_target" && (
              <div className="p-4 space-y-3">
                <p className="text-sm text-gray-500 mb-2">
                  Who should feel the pain?
                </p>
                {otherPlayers.map((player) => {
                  const blocked = isConsecutiveTargetBlocked(
                    roomData,
                    currentUserId,
                    player.id
                  );
                  return (
                    <button
                      key={player.id}
                      onClick={() => !blocked && handleSelectTarget(player)}
                      disabled={blocked}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 font-semibold transition-all text-left ${blocked
                        ? "border-gray-200 text-gray-400 cursor-not-allowed opacity-50"
                        : "border-gray-200 hover:border-red-400 hover:bg-red-50 active:scale-95"
                        }`}
                    >
                      <span>{player.name}</span>
                      {blocked && (
                        <span className="text-xs text-gray-400">3-round limit</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* ── SELECT CATEGORY ── */}
            {step === "select_category" && (
              <div className="p-4 space-y-3">
                <p className="text-sm text-gray-500 mb-2">
                  Which category?
                </p>
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => handleSelectCategory(cat)}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 font-semibold capitalize text-left hover:border-purple-400 hover:bg-purple-50 transition-all active:scale-95"
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}

            {/* ── CONFIRM ── */}
            {step === "confirm" && selectedItem && (
              <div className="p-4">
                <div className="bg-gray-50 rounded-2xl p-5 text-center mb-4">
                  <div className="w-16 h-16 mx-auto mb-2 rounded-2xl flex items-center justify-center bg-gray-100">
                    {(() => {
                      const IconComp = ICON_MAP[selectedItem.iconName];
                      return IconComp ? <IconComp className="w-8 h-8 text-gray-700" /> : <span className="text-4xl">{selectedItem.emoji}</span>;
                    })()}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {selectedItem.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-3">{selectedItem.desc}</p>

                  {selectedTarget && (
                    <div className="bg-white border border-gray-200 rounded-xl px-4 py-2 mb-2 text-sm">
                      <span className="text-gray-500">Target: </span>
                      <span className="font-bold">{selectedTarget.name}</span>
                    </div>
                  )}
                  {selectedCategory && (
                    <div className="bg-white border border-gray-200 rounded-xl px-4 py-2 mb-2 text-sm capitalize">
                      <span className="text-gray-500">Category: </span>
                      <span className="font-bold">{selectedCategory}</span>
                    </div>
                  )}

                  <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-2 text-sm mt-2 flex items-center justify-center gap-1.5">
                    <span className="text-red-700">Cost:</span>
                    <span className="w-4 h-4 bg-red-600 rounded-full flex items-center justify-center text-white text-[8px] font-bold">₦</span>
                    <span className="font-bold text-red-800">
                      {selectedItem.cost} coins
                    </span>
                  </div>

                  {selectedItem.type !== "share_fate" && (
                    <p className="text-xs text-purple-500 mt-2 font-semibold">
                      Activates round {currentRound + 1}
                    </p>
                  )}
                </div>

                <button
                  onClick={handleConfirm}
                  disabled={loading}
                  className={`w-full py-3 rounded-xl font-bold text-white transition-all ${loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-purple-600 hover:bg-purple-700 active:scale-95 shadow-md"
                    }`}
                >
                  {loading ? "Processing..." : `Confirm`}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Nested CoinStore modal */}
      {showCoinStore && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <CoinStore isOpen={true} onClose={() => setShowCoinStore(false)} />
          </div>
        </div>
      )}
    </>
  );
}

export default hiddenInVatar(PowerUpShop);
